'use client';

import React from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import FractionDisplay from '@src/components/Shared/FractionDisplay';
import NextLink from '@src/components/Shared/NextLink';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import { TorqueCardPropTypes } from './TorqueCard.types';

const calculationMethods: UnitTypes[] = [
  { id: 'absolute', name: 'Absolute', unit: 'Nm' },
  { id: 'relative', name: 'Body Mass Normalized', unit: 'Nm/kg' },
];

const TorqueCard: React.FC<TorqueCardPropTypes> = ({}) => {
  const isMobile = useIsMobile();

  // 1️⃣ Calculation method (Absolute vs Body Mass Normalized)
  const [method, setMethod] = React.useState<'absolute' | 'relative'>('absolute');

  // 2️⃣ Input values (and temp for mobile)
  const [forceValue, setForceValue] = React.useState('');
  const [effortArmValue, setEffortArmValue] = React.useState('');
  const [bodyMassValue, setBodyMassValue] = React.useState('');

  const [tempForceValue, setTempForceValue] = React.useState('');
  const [tempEffortArmValue, setTempEffortArmValue] = React.useState('');
  const [tempBodyMassValue, setTempBodyMassValue] = React.useState('');

  // 3️⃣ Units for each input
  const [forceUnit, setForceUnit] = React.useState<'newtons' | 'pounds' | 'kilograms'>('newtons');
  const [lengthUnit, setLengthUnit] = React.useState<'meters' | 'centimeters' | 'inches'>('meters');
  const [massUnit, setMassUnit] = React.useState<'kilograms' | 'pounds'>('kilograms');

  // commit mobile temps to main state
  const handleCalculate = () => {
    setForceValue(tempForceValue);
    setEffortArmValue(tempEffortArmValue);
    if (method === 'relative') {
      setBodyMassValue(tempBodyMassValue);
    }
  };

  // Conversion helpers
  const toNewtons = (val: string, unit: string) => {
    const f = parseFloat(val) || 0;
    if (unit === 'pounds') return f / 0.224809;
    if (unit === 'kilograms') return f / 0.1019716;
    return f;
  };
  const toMeters = (val: string, unit: string) => {
    const f = parseFloat(val) || 0;
    if (unit === 'centimeters') return f / 100;
    if (unit === 'inches') return f / 39.3701;
    return f;
  };
  const toKg = (val: string, unit: string) => {
    const f = parseFloat(val) || 0;
    return unit === 'pounds' ? f / 2.20462 : f;
  };

  // choose mobile vs desktop values
  const fv = isMobile ? tempForceValue : forceValue;
  const av = isMobile ? tempEffortArmValue : effortArmValue;
  const mv = isMobile ? tempBodyMassValue : bodyMassValue;

  // compute torque (for display)
  const torque = React.useMemo(() => {
    const F = toNewtons(fv, forceUnit);
    const L = toMeters(av, lengthUnit);
    if (method === 'absolute') {
      return F * L;
    } else {
      const M = toKg(mv, massUnit);
      return M > 0 ? (F * L) / M : 0;
    }
  }, [fv, av, mv, forceUnit, lengthUnit, massUnit, method]);

  // dynamically pull the unit label from calculationMethods
  const suffix = calculationMethods.find((m) => m.id === method)?.unit ?? '';

  // detect pending changes on mobile (ignore body mass in absolute mode)
  const hasChanges =
    tempForceValue !== forceValue ||
    tempEffortArmValue !== effortArmValue ||
    (method === 'relative' && tempBodyMassValue !== bodyMassValue);

  // format for display and debounce
  const formattedTorque = torque.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const debouncedTorque = useDebounce(formattedTorque, 3000);

  // Fire a tracking event whenever a valid debounced torque (committed state) is computed
  React.useEffect(() => {
    // Skip while mobile has uncommitted changes
    if (isMobile && hasChanges) return;

    // Compute committed torque values
    const F_comm = toNewtons(forceValue, forceUnit);
    const L_comm = toMeters(effortArmValue, lengthUnit);
    let committedTorque = 0;
    if (method === 'absolute') {
      committedTorque = F_comm * L_comm;
    } else {
      const M_comm = toKg(bodyMassValue, massUnit);
      committedTorque = M_comm > 0 ? (F_comm * L_comm) / M_comm : 0;
    }

    // Only track if inputs are non-empty and committedTorque is finite
    const baseCondition =
      forceValue !== '' &&
      effortArmValue !== '' &&
      !isNaN(committedTorque) &&
      isFinite(committedTorque);

    const relativeCondition = method === 'relative' && bodyMassValue !== '';

    if (baseCondition && (method === 'absolute' || (method === 'relative' && relativeCondition))) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_torque',
          value: 1,
        });
      }
    }
    // Only re-run when debouncedTorque changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTorque]);

  return (
    <CardMetric
      id='calculator-torque'
      title='Torque'
      productIcons={[
        {
          alt: 'NordBord App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/6iS357n4siVSm6nFZeci6q/544cd567518357e813ac48f15dae44f4/nordbord-app-icon.svg',
        },
        {
          alt: 'ForceFrame App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/7dxABhvlBmXLF0UuYpAdss/883fc648f9bc2f93d9d05d9095800e67/forceframe-app-icon.svg',
        },
        {
          alt: 'DynaMo App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/5KQtYsCw5caSZvIuohrRmm/c174bb8572e3170009776e40c21f18e9/dynamo-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2.2rem] text-[1.2rem]'>
        Assess isolated capacity of a joint to better compare your clients to research-based
        benchmarks.
      </p>

      <div className='mb-[2.4rem] flex flex-col gap-[0.5rem] sm:flex-row sm:items-center'>
        <UnitDropdown
          units={calculationMethods}
          label=''
          activeUnitId={method}
          onChange={(id) => setMethod(id as 'absolute' | 'relative')}
        />

        {method === 'absolute' ? (
          <FractionDisplay
            className='mb-[2rem]'
            label='Torque'
            numerator="<strong className='font-medium'>(MVIC Assessment)</strong> Peak Force × <strong className='font-medium'>Effort Arm Length</strong>"
          />
        ) : (
          <FractionDisplay
            className='mb-[2rem]'
            label='Torque'
            numerator="<strong className='font-medium'>(MVIC Assessment Peak Force</strong> × Effort Arm Length<strong>)</strong>"
            denominator="<strong className='font-medium'>Body Mass</strong> Kilograms"
          />
        )}
      </div>

      {/* Force Input */}
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['MVIC Assessment', 'Peak Force']}
        placeholder='Newtons'
        units={[{ name: 'Newtons', unit: 'N', id: 'newtons' }]}
        activeUnit={forceUnit}
        value={fv}
        onUnitSelect={(id) => setForceUnit(id as 'newtons' | 'pounds' | 'kilograms')}
        onChange={(e) =>
          isMobile ? setTempForceValue(e.target.value) : setForceValue(e.target.value)
        }
      />

      {/* Effort Arm Input */}
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Effort Arm', 'Joint line to limb attachment']}
        placeholder='Meters'
        units={[{ name: 'Meters', unit: 'm', id: 'meters' }]}
        activeUnit={lengthUnit}
        value={av}
        onUnitSelect={(id) => setLengthUnit(id as 'meters' | 'centimeters' | 'inches')}
        onChange={(e) =>
          isMobile ? setTempEffortArmValue(e.target.value) : setEffortArmValue(e.target.value)
        }
      />

      {/* Body Mass Input (only for relative mode) */}
      {method === 'relative' && (
        <ForceInput
          className='mb-[1.4rem]'
          labelLines={['Body Mass', 'Kilograms']}
          placeholder='Kilograms'
          units={[{ name: 'Kilograms', unit: 'kg', id: 'kilograms' }]}
          activeUnit={massUnit}
          value={mv}
          onUnitSelect={(id) => setMassUnit(id as 'kilograms' | 'pounds')}
          onChange={(e) =>
            isMobile ? setTempBodyMassValue(e.target.value) : setBodyMassValue(e.target.value)
          }
        />
      )}

      {/* Torque result */}
      <div className='mb-[3.1rem] flex items-center justify-between font-normal text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>Joint Torque</span>
        <span className='ml-[2rem] border-l-2 border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {isMobile && hasChanges ? '0.0' : formattedTorque}
          <span className='text-[1.6rem] font-light'>{suffix}</span>
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            Understand the{' '}
            <NextLink
              className='font-light underline'
              href='https://thesciencept.com/why-a-quad-index-matters/'
            >
              key applications
            </NextLink>{' '}
            of torque during knee rehabilitation from Erik Meira.
          </p>
        </div>

        {isMobile && (
          <button
            onClick={handleCalculate}
            disabled={!hasChanges}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default TorqueCard;
